import React, {FC} from 'react';

import collectOpen from 'assets/v3/guide/extension/collect-open.webp';
import collectSelect from 'assets/v3/guide/extension/collect-select.webp';
import collectTranslate1 from 'assets/v3/guide/extension/collect-translate-1.webp';
import collectTranslate2 from 'assets/v3/guide/extension/collect-translate-2.webp';
import devMode from 'assets/v3/guide/extension/dev-mode.webp';
import downloadImg from 'assets/v3/guide/extension/download.webp';
import listingCollect from 'assets/v3/guide/extension/listing-collect.webp';
import listingDownload from 'assets/v3/guide/extension/listing-download.webp';
import listingOpen from 'assets/v3/guide/extension/listing-open.webp';
import listingParse from 'assets/v3/guide/extension/listing-parse.webp';
import listingPreview from 'assets/v3/guide/extension/listing-preview.webp';
import listingSelect from 'assets/v3/guide/extension/listing-select.webp';
import listingSettings from 'assets/v3/guide/extension/listing-settings.webp';
import loadUnpacked from 'assets/v3/guide/extension/load-unpacked.webp';
import pinFloat from 'assets/v3/guide/extension/pin-float.webp';
import pinToolbar from 'assets/v3/guide/extension/pin-toolbar.webp';
import refine1 from 'assets/v3/guide/extension/refine-1.webp';
import refine2 from 'assets/v3/guide/extension/refine-2.webp';
import storeInstall from 'assets/v3/guide/extension/store-install.webp';
import unzipMac from 'assets/v3/guide/extension/unzip-mac.webp';
import unzipWin from 'assets/v3/guide/extension/unzip-win.webp';
import unzippedFiles from 'assets/v3/guide/extension/unzipped-files.webp';

/** 使用指南「浏览器插件」tab 中文正文。2026-09-17 按业务最新版《象寄 AI
 * 插件的安装和使用（图片翻译、详情页生成）》（docx）重写：安装（Chrome 商店 +
 * 手动解压安装）+ 插件采集图片翻译 + 插件采集生成主图/详情图；Chrome 商店与 zip
 * 安装包为外链（带 utm_source=xj-doc，业务既有约定）。 */
const ZhContent: FC = () => {
  return (
    <>
      <h2>如何在「Chrome 等浏览器」安装扩展？</h2>

      <h3>一、Chrome 应用商店安装</h3>
      <p>如果你想从 Chrome 官方扩展商店安装，首先你需要确保你的网络环境能够连线上谷歌的服务（需要代理工具）。</p>
      <ul>
        <li>
          直接打开插件下载链接：
          <br />
          <a
            href="https://chromewebstore.google.com/detail/%E8%B1%A1%E5%AF%84%E5%9B%BE%E7%89%87%E7%BF%BB%E8%AF%91%E5%8A%A9%E6%89%8B/ejkpocbmomaefkinmkfbfnpjjfbinegb?utm_source=xj-doc"
            rel="noreferrer"
            target="_blank"
          >
            <span aria-hidden="true">🔗</span> 去 Chrome 应用商店安装
          </a>
        </li>
        <li>点击右上角「添加至 Chrome」按钮，完成安装。</li>
      </ul>
      <img alt="Chrome 应用商店安装" decoding="async" loading="lazy" src={storeInstall} />

      <h3>二、下载安装包，手动安装</h3>
      <p>
        1. 点击下方压缩包，下载最新版本插件安装包：
        <br />
        <a href="https://i.tosoiot.com/assets/xj-chrome-ext.zip" rel="noreferrer" target="_blank">
          <span aria-hidden="true">📥</span> 点击下载浏览器插件
        </a>
      </p>
      <p>2. 下载完成后，解压文件。下载文件为 .zip 文件，解压后是一个文件夹。</p>
      <p>Mac 版本</p>
      <img alt="Mac 解压后的文件夹" decoding="async" loading="lazy" src={unzipMac} />
      <p>Win 版本</p>
      <img alt="Windows 解压后的文件夹" decoding="async" loading="lazy" src={unzipWin} />
      <p>文件夹内文件如下：</p>
      <img alt="插件文件夹内容" decoding="async" loading="lazy" src={unzippedFiles} />
      <p>3. 打开 chrome「管理扩展程序」页面，进入「管理扩展程序」。</p>
      <ul>
        <li>方式一：在地址栏输入 chrome://extensions/</li>
        <li>方式二：点击浏览器右上角，打开浏览器设置，找到扩展程序</li>
      </ul>
      <p>点击右上角开启「开发者模式」。</p>
      <img alt="开启开发者模式" decoding="async" loading="lazy" src={devMode} />
      <p>4. 点击「加载已解压的扩展程序」。点击按钮，在弹窗中选择刚刚解压的文件夹，确认后，插件即导入成功。</p>
      <img alt="加载已解压的扩展程序" decoding="async" loading="lazy" src={loadUnpacked} />
      <p>导入成功后，可以设置插件置顶和桌面悬浮标，便捷操作，刷新页面后就可采集使用了～</p>
      <img alt="设置插件置顶" decoding="async" loading="lazy" src={pinToolbar} />
      <img alt="桌面悬浮标" decoding="async" loading="lazy" src={pinFloat} />

      <h2>如何使用插件采集图片进行翻译？</h2>

      <h3>1. 打开需要翻译的产品链接</h3>
      <img alt="打开需要翻译的产品链接" decoding="async" loading="lazy" src={collectOpen} />

      <h3>2. 选择图片翻译，进行产品图片采集</h3>
      <p>点击扩展程序或悬浮按钮，选择图片翻译，进行产品图片采集。</p>
      <img alt="选择图片翻译" decoding="async" loading="lazy" src={collectSelect} />
      <p>按需选择需要翻译的图片，选择好目标语言后点击翻译，开始对产品图片进行翻译处理。</p>
      <img alt="选择需要翻译的图片" decoding="async" loading="lazy" src={collectTranslate1} />
      <img alt="开始翻译采集的图片" decoding="async" loading="lazy" src={collectTranslate2} />

      <h3>3. 翻译后图片的下载与精修</h3>
      <p>可点击单张图片放大查看翻译效果，满意可批量下载到本地。</p>
      <img alt="查看并批量下载译图" decoding="async" loading="lazy" src={downloadImg} />
      <p>不满意可点击精修，跳转至象寄官网进行二次编辑调整。</p>
      <img alt="点击精修" decoding="async" loading="lazy" src={refine1} />
      <img alt="官网精修编辑" decoding="async" loading="lazy" src={refine2} />

      <h2>如何使用插件采集商品图片进行主图/详情图生成？</h2>

      <h3>1. 打开需要生成详情页或者主图的产品链接</h3>
      <img alt="打开需要生成主图/详情页的产品链接" decoding="async" loading="lazy" src={listingOpen} />

      <h3>2. 选择详情页生成，进行产品图片采集</h3>
      <p>点击扩展程序或悬浮按钮，选择详情页生成，进行产品图片采集。</p>
      <img alt="选择详情页生成" decoding="async" loading="lazy" src={listingCollect} />
      <p>按需选择需要使用的图片（产品细节图、白底图、角度图和使用场景图），点击详情页生成。</p>
      <img alt="选择需要使用的图片" decoding="async" loading="lazy" src={listingSelect} />
      <p>点击详情页生成后，会对产品信息和选择图片自动进行解析，大概几秒后自动跳转到象寄工作台。</p>
      <img alt="解析产品信息并跳转工作台" decoding="async" loading="lazy" src={listingParse} />
      <p>根据产品需求完善生成设置。</p>
      <img alt="完善生成设置" decoding="async" loading="lazy" src={listingSettings} />
      <p>我们会在极短时间内生成预览套图，用于对风格和效果的确认，不满意可再次生成，满意点击生成最终高清图即可。</p>
      <img alt="生成预览套图" decoding="async" loading="lazy" src={listingPreview} />

      <h3>3. 下载</h3>
      <img alt="下载生成结果" decoding="async" loading="lazy" src={listingDownload} />
    </>
  );
};

export default ZhContent;
